import { supabase } from './supabase';

export const getCrmSavedViews = async (viewKey = 'crm_contacts') => {
  const { data, error } = await supabase
    .from('crm_saved_views')
    .select('*')
    .eq('view_key', viewKey)
    .order('is_default', { ascending: false })
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const saveCrmView = async ({ id, name, viewKey = 'crm_contacts', columns, filters, sort, isDefault = false }) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not logged in');
  const payload = {
    ...(id ? { id } : {}),
    owner_user_id: user.id,
    name,
    view_key: viewKey,
    columns,
    filters,
    sort,
    is_default: isDefault,
  };
  const { data, error } = await supabase
    .from('crm_saved_views')
    .upsert(payload, { onConflict: 'id' })
    .select('*')
    .single();
  if (error) throw error;
  return data;
};

export const deleteCrmView = async (id) => {
  const { error } = await supabase.from('crm_saved_views').delete().eq('id', id);
  if (error) throw error;
};

export const getCrmTags = async () => {
  const { data, error } = await supabase
    .from('crm_tags')
    .select('*')
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const createCrmTag = async ({ name, color = '#5B9DFF' }) => {
  const { data, error } = await supabase
    .from('crm_tags')
    .upsert({ name: name.trim(), color }, { onConflict: 'name' })
    .select('*')
    .single();
  if (error) throw error;
  return data;
};

export const getCrmContactTags = async () => {
  const { data, error } = await supabase
    .from('crm_contact_tags')
    .select('contact_id, tag_id, crm_tags(id, name, color)');
  if (error) throw error;
  return data || [];
};

export const addCrmTagToContacts = async (contactIds, tagId) => {
  const rows = [...new Set(contactIds)]
    .filter(Boolean)
    .map((contact_id) => ({ contact_id, tag_id: tagId }));
  if (!rows.length) return [];
  const { data, error } = await supabase
    .from('crm_contact_tags')
    .upsert(rows, { onConflict: 'contact_id,tag_id' })
    .select('*');
  if (error) throw error;
  return data || [];
};

export const sendCrmEmail = async ({ subject, body, recipients, attachments = [] }) => {
  const { data, error } = await supabase.functions.invoke('crm-send-email', {
    body: { subject, body, recipients, attachments },
  });
  if (error) {
    let message = error.message || 'CRM email could not be sent.';
    if (error.context && typeof error.context.json === 'function') {
      try {
        const details = await error.context.json();
        message = details?.error || details?.message || message;
      } catch {
        // Keep the Supabase error message when the response body is not JSON.
      }
    }
    throw new Error(message);
  }
  if (data?.failedCount > 0) {
    const failureSummary = (data.deliveries || [])
      .filter((delivery) => delivery.status === 'failed')
      .map((delivery) => `${delivery.email}: ${delivery.error || 'failed'}`)
      .join('; ');
    throw new Error(failureSummary || `${data.failedCount} CRM email recipient(s) failed.`);
  }
  return data;
};

export const syncCrmInboundEmail = async ({ limit = 25 } = {}) => {
  const { data, error } = await supabase.functions.invoke('crm-sync-inbound-email', {
    body: { limit },
  });
  if (error) {
    let message = error.message || 'Inbound email sync failed.';
    if (error.context && typeof error.context.json === 'function') {
      try {
        const details = await error.context.json();
        message = details?.error || details?.message || message;
      } catch {
        // Keep the Supabase error message when the response body is not JSON.
      }
    }
    throw new Error(message);
  }
  return data;
};

export const updateCrmMemberContact = async ({ contactId, memberUserId, name, email, phone }) => {
  const normalizedName = String(name || '').trim();
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedPhone = String(phone || '').trim();

  if (contactId) {
    const { error } = await supabase
      .from('crm_contacts')
      .update({
        full_name: normalizedName || null,
        email: normalizedEmail || null,
        phone: normalizedPhone || null,
      })
      .eq('id', contactId);
    if (error) throw error;
  }

  if (memberUserId) {
    const { error } = await supabase
      .from('profiles')
      .update({
        username: normalizedName || null,
        phone: normalizedPhone || null,
      })
      .eq('id', memberUserId);
    if (error) throw error;
  }

  return { status: 'updated' };
};

export const getCrmMessageThreads = async () => {
  const { data, error } = await supabase
    .from('crm_message_threads')
    .select('*, crm_messages(id, sender_user_id, sender_role, body, created_at)')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const getCrmThreadMessages = async (threadId) => {
  const { data, error } = await supabase
    .from('crm_messages')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const sendCrmMessage = async ({ threadId, memberUserId, investorId, subscriptionId, subject, body, senderRole = 'manager' }) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not logged in');
  let resolvedThreadId = threadId;
  if (!resolvedThreadId) {
    const { data: thread, error: threadError } = await supabase
      .from('crm_message_threads')
      .insert({
        member_user_id: memberUserId,
        investor_id: investorId || null,
        subscription_id: subscriptionId || null,
        subject: subject || 'Member message',
        created_by: user.id,
      })
      .select('*')
      .single();
    if (threadError) throw threadError;
    resolvedThreadId = thread.id;
  }

  const { data, error } = await supabase
    .from('crm_messages')
    .insert({
      thread_id: resolvedThreadId,
      sender_user_id: user.id,
      sender_role: senderRole,
      body,
    })
    .select('*')
    .single();
  if (error) throw error;

  await supabase
    .from('crm_message_threads')
    .update({ updated_at: new Date().toISOString(), status: 'open' })
    .eq('id', resolvedThreadId);

  return { threadId: resolvedThreadId, message: data };
};

export const markCrmThreadRead = async (threadId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase
    .from('crm_message_reads')
    .upsert({
      thread_id: threadId,
      user_id: user.id,
      last_read_at: new Date().toISOString(),
    }, { onConflict: 'thread_id,user_id' });
  if (error) throw error;
};

export const getUnreadCrmMessageSummary = async ({ manager = false } = {}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { count: 0, latestThreadId: null };

  let threadQuery = supabase
    .from('crm_message_threads')
    .select('id, member_user_id, updated_at, crm_messages(id, sender_user_id, sender_role, created_at)');
  if (!manager) threadQuery = threadQuery.eq('member_user_id', user.id);

  const [{ data: threads, error: threadError }, { data: reads, error: readError }] = await Promise.all([
    threadQuery,
    supabase.from('crm_message_reads').select('*').eq('user_id', user.id),
  ]);
  if (threadError) throw threadError;
  if (readError) throw readError;

  const readByThread = new Map((reads || []).map((read) => [read.thread_id, read.last_read_at]));
  const unreadThreads = (threads || [])
    .map((thread) => {
      const lastIncoming = (thread.crm_messages || [])
        .filter((message) => manager ? message.sender_role === 'member' : message.sender_role === 'manager')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      if (!lastIncoming) return null;
      const readAt = readByThread.get(thread.id);
      if (readAt && new Date(readAt) >= new Date(lastIncoming.created_at)) return null;
      return { threadId: thread.id, createdAt: lastIncoming.created_at };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return {
    count: unreadThreads.length,
    latestThreadId: unreadThreads[0]?.threadId || null,
  };
};
